import { useMemo, useState } from "react";
import { Card, Select, InputNumber, Switch, Tooltip, Empty, Tag } from "antd";
import {
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  PlusOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import MondayButton from "../../commonComponents/MondayButton/MondayButton";
import { LEAVE_RULE_OPTIONS } from "../aquamanConstants";

// Participants and rotation settings. The queue order here is the tie-break the
// backend uses when two people have served the same number of weeks.
const RosterPanel = ({ roster, saving, onSaveRoster }) => {
  const { isDarkMode } = useSelector((state) => state.usersList);
  const [toAdd, setToAdd] = useState([]);

  const order = useMemo(() => {
    const byId = new Map((roster?.participants || []).map((p) => [p.employeeId, p]));
    const ordered = (roster?.order || [])
      .map((id) => byId.get(id))
      .filter(Boolean);
    // Anyone missing from `order` (added by a script, or deactivated) still has
    // to be visible, otherwise an admin cannot remove them.
    for (const p of roster?.participants || []) {
      if (!ordered.some((o) => o.employeeId === p.employeeId)) ordered.push(p);
    }
    return ordered;
  }, [roster]);

  const move = (index, delta) => {
    const ids = order.map((p) => p.employeeId);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    onSaveRoster({ order: ids }, "Rendi u ruajt.");
  };

  const toggleActive = (participant) => {
    onSaveRoster(
      {
        participants: (roster.participants || []).map((p) =>
          p.employeeId === participant.employeeId
            ? { ...p, active: p.active === false }
            : p
        ),
      },
      participant.active === false
        ? `${participant.employeeName} u aktivizua.`
        : `${participant.employeeName} u pezullua.`
    );
  };

  return (
    <Card
      className={`aquamanCard ${isDarkMode ? "dark" : ""}`}
      title="Lista e rotacionit"
      extra={
        <span className="aquamanCardMeta">
          {order.filter((p) => p.active !== false).length} aktivë /{" "}
          {order.length} gjithsej
        </span>
      }
    >
      <div className="aquamanSettingsRow">
        <label>
          <span>Ditë pushim që e kalojnë javën</span>
          <Select
            value={roster?.leaveRule || "most_of_week"}
            options={LEAVE_RULE_OPTIONS}
            onChange={(leaveRule) => onSaveRoster({ leaveRule })}
            disabled={saving}
            style={{ width: 220 }}
          />
        </label>
        <label>
          <Tooltip title="Sa javë pas shërbimit personi nuk rikthehet në short, kur ka të tjerë në dispozicion.">
            <span>Pauzë pas radhës (javë)</span>
          </Tooltip>
          <InputNumber
            min={0}
            max={52}
            value={roster?.cooldownWeeks ?? 0}
            onChange={(cooldownWeeks) => onSaveRoster({ cooldownWeeks })}
            disabled={saving}
          />
        </label>
        <label>
          <span>Javë që planifikohen</span>
          <InputNumber
            min={1}
            max={52}
            value={roster?.horizonWeeks ?? 12}
            onChange={(horizonWeeks) => onSaveRoster({ horizonWeeks })}
            disabled={saving}
          />
        </label>
        <label>
          <span>Njoftim automatik</span>
          <Switch
            checked={roster?.notifyEnabled !== false}
            onChange={(notifyEnabled) => onSaveRoster({ notifyEnabled })}
            disabled={saving}
          />
        </label>
      </div>

      <div className="aquamanAddRow">
        <Select
          mode="multiple"
          allowClear
          placeholder="Shto punonjës nga skuadra"
          value={toAdd}
          onChange={setToAdd}
          style={{ flex: 1 }}
          optionFilterProp="label"
          options={(roster?.candidatePool || []).map((c) => ({
            value: c.employeeId,
            label: c.employeeName,
          }))}
        />
        <MondayButton
          Icon={<UserAddOutlined />}
          hasIcon
          disabled={saving || toAdd.length === 0}
          onClick={() =>
            onSaveRoster({ addEmployeeIds: toAdd }, "Punonjësit u shtuan.").then(
              () => setToAdd([])
            )
          }
        >
          Shto
        </MondayButton>
        <Tooltip title="Shton të gjithë punonjësit aktivë të skuadrës që plotësojnë filtrin dhe nuk janë në listë.">
          <MondayButton
            className="mondayButtonBlue"
            Icon={<PlusOutlined />}
            hasIcon
            disabled={saving}
            onClick={() => onSaveRoster({ fillFromTeam: true }, "Lista u plotësua.")}
          >
            Plotëso nga skuadra
          </MondayButton>
        </Tooltip>
      </div>

      {order.length === 0 ? (
        <Empty description="Nuk ka punonjës në rotacion" />
      ) : (
        <ol className="aquamanRosterList">
          {order.map((p, idx) => (
            <li
              key={p.employeeId}
              className={p.active === false ? "inactive" : ""}
            >
              <span className="position">{idx + 1}</span>
              <span className="name">
                <span className="nameText">{p.employeeName}</span>
                {p.active === false ? <Tag color="default">pezulluar</Tag> : null}
              </span>
              <span className="served">{p.servedCount || 0} javë</span>
              <span className="actions">
                <Tooltip title="Ngjit lart">
                  <ArrowUpOutlined onClick={() => move(idx, -1)} />
                </Tooltip>
                <Tooltip title="Zbrit poshtë">
                  <ArrowDownOutlined onClick={() => move(idx, 1)} />
                </Tooltip>
                <Tooltip
                  title={
                    p.active === false ? "Riaktivizo" : "Pezullo pa fshirë historikun"
                  }
                >
                  <Switch
                    size="small"
                    checked={p.active !== false}
                    onChange={() => toggleActive(p)}
                  />
                </Tooltip>
                <Tooltip title="Hiq nga lista">
                  <DeleteOutlined
                    onClick={() =>
                      onSaveRoster(
                        { removeEmployeeIds: [p.employeeId] },
                        `${p.employeeName} u hoq nga lista.`
                      )
                    }
                  />
                </Tooltip>
              </span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
};

export default RosterPanel;
